import { Injectable, OnDestroy } from '@angular/core';
import { Location } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, Subscription, interval, of } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { TimerEntryService } from '@intranet/modules/timer/timer-entry.service';
import { DesktopAppService } from '@intranet/core/services/desktop-app.service';
import { PendingWritesTracker } from '@intranet/core/http/pending-writes.interceptor';

/** Shape of /assets/version.json, written by ClientApp/scripts/write-version.mjs. */
interface BuildVersion {
    hash: string;
    sha: string;
    builtAt: string;
}

/**
 * Public state for the refresh banner.
 * - idle:        no newer build seen (or service still establishing baseline).
 * - available:   a newer build has been detected on the server. The banner
 *                renders, the user can click Refresh, and the service will
 *                also auto-reload at the next "safe moment" (currently
 *                defined as the timer stopping). Reload is silent at that
 *                point — the user is at a natural break.
 */
export type RefreshState = 'idle' | 'available';

/**
 * Polls /assets/version.json so the running app can notice when a new
 * Angular bundle has been deployed. Used by the Visarc Timer Electron
 * shell — and any long-lived tab — to surface a "Refresh" prompt rather
 * than letting users serve a stale UI for days.
 *
 * Step 3 of the rollout: detection + banner + opportunistic auto-reload.
 * The banner is shown in the desktop shell only. In the browser there's no
 * banner — instead the user's next page change becomes a full page load
 * (see reloadForNavigation / NewVersionReloadGuard).
 */
@Injectable({ providedIn: 'root' })
export class BuildVersionService implements OnDestroy {
    /** Snapshot taken on first successful fetch. New polls compare against this. */
    private baselineHash: string | null = null;
    /** Most recently observed hash. Used so we don't re-log the same change every poll. */
    private lastSeenHash: string | null = null;
    /** Public state stream — banner subscribes to this. Once 'available'
     *  it stays 'available' until the page reloads (which re-baselines). */
    private readonly _state$ = new BehaviorSubject<RefreshState>('idle');
    readonly state$: Observable<RefreshState> = this._state$.asObservable();

    private pollSub: Subscription | null = null;
    private stopSub: Subscription | null = null;
    private focusHandler: (() => void) | null = null;

    private readonly POLL_MS = 5 * 60 * 1000; // 5 minutes
    private readonly URL = 'assets/version.json';

    /** Set once reloadForNavigation has started a page load on this page. */
    private navReloadAttempted = false;
    /** sessionStorage key holding when reloadForNavigation last fired. */
    private static readonly NAV_RELOAD_KEY = 'visarc-new-version-reload-at';
    private static readonly NAV_RELOAD_COOLDOWN_MS = 10 * 60 * 1000; // 10 minutes

    constructor(
        private http: HttpClient,
        private timerEntryService: TimerEntryService,
        private desktopApp: DesktopAppService,
        private pendingWrites: PendingWritesTracker,
        private location: Location,
    ) {
        this.start();
    }

    /** Force a reload now. Called by the banner's "Refresh" button. */
    reload(): void {
        window.location.reload();
    }

    /** Current state (synchronous read for non-subscribing callers). */
    get currentState(): RefreshState {
        return this._state$.value;
    }

    /**
     * Browser-only: if a newer build has been detected, load `url` as a full
     * page instead of letting the router navigate in-app, so a tab that's
     * been left open picks up the new bundle on the user's next page change.
     * Called by NewVersionReloadGuard. Returns true if a page load was
     * started — the caller should then cancel the in-app navigation.
     *
     * Holds off (normal in-app navigation, try again next time) when:
     * - running in the desktop shell — it has its own banner + auto-reload;
     * - a write request is still in flight — a page load would abort it;
     * - we already tried once on this page (e.g. the user chose "Stay" on a
     *   beforeunload prompt) — never trap them on the current page;
     * - we auto-reloaded within the cooldown — guards against a reload loop
     *   if the server ever reports a flapping version.
     */
    reloadForNavigation(url: string, replaceHistoryEntry: boolean): boolean {
        if (this._state$.value !== 'available') return false;
        if (this.desktopApp.isTimerApp) return false;
        if (this.navReloadAttempted) return false;
        if (this.pendingWrites.hasPendingWrites) return false;
        if (this.inNavReloadCooldown()) return false;

        this.navReloadAttempted = true;
        try {
            sessionStorage.setItem(BuildVersionService.NAV_RELOAD_KEY, String(Date.now()));
        } catch {
            // Storage unavailable — reload anyway, we just lose the loop guard.
        }
        const target = this.location.prepareExternalUrl(url);
        console.log('[build-version] new bundle available — full page load to', target);
        if (replaceHistoryEntry) {
            window.location.replace(target);
        } else {
            window.location.assign(target);
        }
        return true;
    }

    private inNavReloadCooldown(): boolean {
        try {
            const last = Number(sessionStorage.getItem(BuildVersionService.NAV_RELOAD_KEY));
            return Number.isFinite(last) && Date.now() - last < BuildVersionService.NAV_RELOAD_COOLDOWN_MS;
        } catch {
            return false;
        }
    }

    private start(): void {
        // Take the baseline first. Until the baseline arrives, polls are
        // no-ops (otherwise the first poll after the baseline fetch would
        // race and could mis-report a "change").
        this.fetchOnce().then(v => {
            if (v) {
                this.baselineHash = v.hash;
                this.lastSeenHash = v.hash;
                console.log('[build-version] baseline:', v.hash, '(built', v.builtAt + ')');
            } else {
                console.log('[build-version] could not fetch /assets/version.json — running locally without a build? File missing on server?');
            }
        });

        // Periodic poll. NgZone-stable interval is fine — this isn't UI-bound.
        this.pollSub = interval(this.POLL_MS).pipe(
            switchMap(() => this.http.get<BuildVersion>(this.cacheBust(), { responseType: 'json' as const })
                .pipe(catchError(() => of(null)))),
        ).subscribe(v => this.handle(v));

        // Re-check the moment the window regains focus. Catches the common
        // case of the user leaving the app open for hours, deploying in
        // the meantime, then clicking back in — they shouldn't have to wait
        // up to POLL_MS for the next tick.
        this.focusHandler = () => this.checkNow();
        window.addEventListener('focus', this.focusHandler);

        // Opportunistic auto-reload: when the user stops a timer, that's a
        // natural break point with no in-flight work. If a refresh is
        // already pending, reload then so they pick up the new bundle
        // without having to click the banner.
        //
        // Electron-only: the banner is mounted only in the desktop shell,
        // so browser users have no UI cueing them to expect a reload —
        // auto-reloading them would feel like a surprise. They pick up the
        // new bundle on their next page change instead (reloadForNavigation).
        if (this.desktopApp.isTimerApp) {
            this.stopSub = this.timerEntryService.stopEntry$.subscribe(() => {
                if (this._state$.value === 'available') {
                    console.log('[build-version] auto-reloading at timer-stop');
                    // Delay so the stop-timer persist call has time to
                    // flush before we reload (reload cancels in-flight
                    // XHRs, which would orphan the timer entry server-side
                    // on slow networks). 1500ms still feels instantaneous
                    // to the user.
                    setTimeout(() => this.reload(), 1500);
                }
            });
        }
    }

    /** One-off fetch used for the initial baseline. */
    private fetchOnce(): Promise<BuildVersion | null> {
        return new Promise(resolve => {
            this.http.get<BuildVersion>(this.cacheBust(), { responseType: 'json' as const })
                .pipe(catchError(() => of(null)))
                .subscribe(v => resolve(v));
        });
    }

    /** Manually re-check (used by the focus listener). */
    private checkNow(): void {
        this.http.get<BuildVersion>(this.cacheBust(), { responseType: 'json' as const })
            .pipe(catchError(() => of(null)))
            .subscribe(v => this.handle(v));
    }

    private handle(v: BuildVersion | null): void {
        if (!v) return;
        if (!this.baselineHash) {
            // Baseline still arriving — adopt this as the baseline rather
            // than treating it as a "change" against a null baseline.
            this.baselineHash = v.hash;
            this.lastSeenHash = v.hash;
            return;
        }
        if (v.hash === this.lastSeenHash) return;
        this.lastSeenHash = v.hash;

        if (v.hash !== this.baselineHash && this._state$.value === 'idle') {
            console.log(
                '[build-version] new bundle detected — baseline:',
                this.baselineHash,
                'now:',
                v.hash,
                '(built',
                v.builtAt + ')',
            );
            this._state$.next('available');
        }
    }

    /** Bust the HTTP cache so IIS / browser don't serve a stale copy. The
     *  query param is harmless — Angular's static-asset handler ignores it. */
    private cacheBust(): string {
        return `${this.URL}?t=${Date.now()}`;
    }

    ngOnDestroy(): void {
        if (this.pollSub) {
            this.pollSub.unsubscribe();
            this.pollSub = null;
        }
        if (this.stopSub) {
            this.stopSub.unsubscribe();
            this.stopSub = null;
        }
        if (this.focusHandler) {
            window.removeEventListener('focus', this.focusHandler);
            this.focusHandler = null;
        }
    }
}
